import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Card,
  CardContent
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday,
  Add as AddIcon
} from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import api from '../../utils/api';

interface Counsellor {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  onboardingCompletedAt: string;
  inductionScheduled: boolean;
  inductionScheduledDate?: string;
  inductionId?: string;
}

interface InductionAttendee {
  counsellorId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  attended: boolean;
  attendedAt?: string;
}

interface InductionSession {
  _id: string;
  scheduledDate: string;
  endDate: string;
  meetingId: string;
  meetingLink: string;
  attendees: InductionAttendee[];
  createdBy: any;
  createdAt: string;
  rescheduled: boolean;
  rescheduledFrom?: string;
  cancelled: boolean;
  notes?: string;
}


interface InductionManagementProps {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const InductionManagement: React.FC<InductionManagementProps> = ({ onSuccess, onError }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [eligibleCounsellors, setEligibleCounsellors] = useState<Counsellor[]>([]);
  const [sessions, setSessions] = useState<InductionSession[]>([]);
  const [selectedCounsellors, setSelectedCounsellors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [createSessionDialogOpen, setCreateSessionDialogOpen] = useState(false);
  const [addCounsellorsDialogOpen, setAddCounsellorsDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<InductionSession | null>(null);
  const [scheduledDate, setScheduledDate] = useState<Date | null>(new Date());
  const [notes, setNotes] = useState('');

  const fetchEligibleCounsellors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/induction/eligible-counsellors');
      setEligibleCounsellors(response.data);
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to fetch eligible counsellors');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  const fetchSessions = useCallback(async () => {
    try {
      const response = await api.get('/api/induction/sessions');
      setSessions(response.data);
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to fetch induction sessions');
    }
  }, [onError]);

  useEffect(() => {
    fetchEligibleCounsellors();
    fetchSessions();
  }, [fetchEligibleCounsellors, fetchSessions]);

  const handleSelectCounsellor = (counsellorId: string) => {
    setSelectedCounsellors(prev =>
      prev.includes(counsellorId)
        ? prev.filter(id => id !== counsellorId)
        : [...prev, counsellorId]
    );
  };

  const handleSelectAll = () => {
    if (!selectedSession) return;
    
    // Get counsellors not already in the session
    const existingIds = selectedSession.attendees.map(a => a.counsellorId._id);
    const availableCounsellors = eligibleCounsellors.filter(c => !existingIds.includes(c._id));
    
    if (selectedCounsellors.length === availableCounsellors.length) {
      setSelectedCounsellors([]);
    } else {
      setSelectedCounsellors(availableCounsellors.map(c => c._id));
    }
  };

  const handleCreateSession = async () => {
    if (!scheduledDate) {
      onError('Please select a date and time');
      return;
    }

    try {
      setLoading(true);
      await api.post('/api/induction/schedule', {
        scheduledDate: scheduledDate.toISOString(),
        notes
      });

      onSuccess('Induction session created successfully');
      setCreateSessionDialogOpen(false);
      setScheduledDate(new Date());
      setNotes('');
      fetchSessions();
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to create induction session');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCounsellors = async () => {
    if (!selectedSession || selectedCounsellors.length === 0) {
      onError('Please select at least one counsellor');
      return;
    }

    try {
      setLoading(true);
      await api.post(`/api/induction/add-counsellors/${selectedSession._id}`, {
        counsellorIds: selectedCounsellors
      });

      onSuccess(`${selectedCounsellors.length} counsellor(s) added and invitation emails sent`);
      setAddCounsellorsDialogOpen(false);
      setSelectedCounsellors([]);
      setSelectedSession(null);
      fetchEligibleCounsellors();
      fetchSessions();
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to add counsellors');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCounsellor = async (inductionId: string, counsellorId: string) => {
    if (!window.confirm('Are you sure you want to remove this counsellor from the induction session?')) {
      return;
    }

    try {
      await api.delete(`/api/induction/remove-counsellor/${inductionId}/${counsellorId}`);
      onSuccess('Counsellor removed from induction session');
      fetchEligibleCounsellors();
      fetchSessions();
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to remove counsellor');
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to delete this induction session? This cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/api/induction/session/${sessionId}`);
      onSuccess('Induction session deleted successfully');
      fetchSessions();
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to delete session');
    }
  };

  const handleRescheduleInduction = async () => {
    if (!scheduledDate || !selectedSession) {
      onError('Please select a date');
      return;
    }

    try {
      setLoading(true);
      await api.put(`/api/induction/reschedule/${selectedSession._id}`, {
        scheduledDate: scheduledDate.toISOString()
      });

      onSuccess('Induction rescheduled successfully and emails sent to counsellors');
      setRescheduleDialogOpen(false);
      setSelectedSession(null);
      setScheduledDate(new Date());
      fetchEligibleCounsellors();
      fetchSessions();
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to reschedule induction');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAttended = async (inductionId: string, counsellorId: string, attended: boolean) => {
    try {
      await api.put(`/api/induction/mark-attended/${inductionId}/${counsellorId}`, {
        attended
      });

      onSuccess(`Counsellor marked as ${attended ? 'attended' : 'not attended'}`);
      fetchEligibleCounsellors();
      fetchSessions();
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to mark attendance');
    }
  };

  const openRescheduleDialog = (session: InductionSession) => {
    setSelectedSession(session);
    setScheduledDate(new Date(session.scheduledDate));
    setRescheduleDialogOpen(true);
  };

  const openAddCounsellorsDialog = (session: InductionSession) => {
    setSelectedSession(session);
    setSelectedCounsellors([]);
    setAddCounsellorsDialogOpen(true);
  };

  const upcomingSessions = sessions.filter(s => !s.cancelled && new Date(s.scheduledDate) >= new Date());
  const pastSessions = sessions.filter(s => !s.cancelled && new Date(s.scheduledDate) < new Date());

  return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2" className="theme-text-primary">
            Induction Management
          </Typography>
        </Box>

        <div className="border-b border-white/20 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex">
              <button
                className={`py-4 px-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 0
                    ? 'border-blue-400 text-blue-400'
                    : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                }`}
                onClick={() => setActiveTab(0)}
              >
                Induction Sessions
              </button>
              <button
                className={`py-4 px-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 1
                    ? 'border-blue-400 text-blue-400'
                    : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                }`}
                onClick={() => setActiveTab(1)}
              >
                Eligible Counsellors
              </button>
              <button
                className={`py-4 px-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 2
                    ? 'border-blue-400 text-blue-400'
                    : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                }`}
                onClick={() => setActiveTab(2)}
              >
                History
              </button>
            </div>
            {activeTab === 0 && (
              <Button
                variant="contained"
                startIcon={<ScheduleIcon />}
                onClick={() => setCreateSessionDialogOpen(true)}
                sx={{
                  backgroundColor: 'rgba(33, 150, 243, 0.8)',
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 1)'
                  }
                }}
              >
                Create New Session
              </Button>
            )}
          </div>
        </div>

        {/* Induction Sessions Tab */}
        {activeTab === 0 && (
          <Box>
            {upcomingSessions.length === 0 ? (
              <Alert severity="info">
                No upcoming induction sessions. Click "Create New Session" to schedule one.
              </Alert>
            ) : (
              upcomingSessions.map((session) => (
                <Card key={session._id} sx={{ mb: 3, backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {new Date(session.scheduledDate).toLocaleDateString('en-GB', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })} at {new Date(session.scheduledDate).toLocaleTimeString('en-GB', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                      <Box>
                        <IconButton
                          onClick={() => openAddCounsellorsDialog(session)}
                          sx={{ color: 'rgba(76, 175, 80, 0.9)', mr: 1 }}
                          title="Add Counsellors"
                        >
                          <AddIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => openRescheduleDialog(session)}
                          sx={{ color: 'rgba(100, 181, 246, 0.9)', mr: 1 }}
                          title="Reschedule"
                        >
                          <EditIcon />
                        </IconButton>
                        {session.attendees.length === 0 && (
                          <IconButton
                            onClick={() => handleDeleteSession(session._id)}
                            sx={{ color: 'rgba(239, 83, 80, 0.9)' }}
                            title="Delete Session"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    {session.rescheduled && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Rescheduled from {new Date(session.rescheduledFrom!).toLocaleDateString()}
                      </Alert>
                    )}

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        <strong>Google Meet Link:</strong> <a href={session.meetingLink} target="_blank" rel="noopener noreferrer" style={{ color: '#64b5f6' }}>{session.meetingLink}</a>
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.875rem', mt: 0.5 }}>
                        Click the link to join. No password required.
                      </Typography>
                    </Box>

                    <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
                      Attendees ({session.attendees.length}):
                    </Typography>

                    {session.attendees.length === 0 ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        No counsellors added yet. Click the + icon above to add counsellors to this session.
                      </Alert>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Name</TableCell>
                              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Email</TableCell>
                              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Attended?</TableCell>
                              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Remove</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {session.attendees.map((attendee) => (
                              <TableRow key={attendee.counsellorId._id}>
                                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                  {attendee.counsellorId.firstName} {attendee.counsellorId.lastName}
                                </TableCell>
                                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                  {attendee.counsellorId.email}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={attendee.attended}
                                    onChange={(e) => handleMarkAttended(session._id, attendee.counsellorId._id, e.target.checked)}
                                    icon={<CancelIcon />}
                                    checkedIcon={<CheckCircleIcon />}
                                    sx={{
                                      color: 'rgba(239, 83, 80, 0.9)',
                                      '&.Mui-checked': {
                                        color: 'rgba(76, 175, 80, 0.9)'
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveCounsellor(session._id, attendee.counsellorId._id)}
                                    sx={{ color: 'rgba(239, 83, 80, 0.9)' }}
                                    title="Remove from session"
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        )}

        {/* Eligible Counsellors Tab */}
        {activeTab === 1 && (
          <Box>
            <Card sx={{ mb: 3, backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent>
                <Typography variant="body1" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  Counsellors who have completed onboarding and are ready for induction.
                </Typography>
              </CardContent>
            </Card>

            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}>
              {eligibleCounsellors.length} Eligible Counsellor(s)
            </Typography>
            {eligibleCounsellors.length === 0 ? (
              <Alert severity="info">No counsellors are currently eligible for induction.</Alert>
            ) : (
              <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Table>
                  <TableHead sx={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <Checkbox
                          checked={selectedCounsellors.length === eligibleCounsellors.length}
                          indeterminate={selectedCounsellors.length > 0 && selectedCounsellors.length < eligibleCounsellors.length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Name</TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Email</TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Onboarding Completed</TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eligibleCounsellors.map((counsellor) => (
                      <TableRow key={counsellor._id} hover>
                        <TableCell padding="checkbox" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <Checkbox
                            checked={selectedCounsellors.includes(counsellor._id)}
                            onChange={() => handleSelectCounsellor(counsellor._id)}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          {counsellor.firstName} {counsellor.lastName}
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          {counsellor.email}
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          {new Date(counsellor.onboardingCompletedAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          {counsellor.inductionScheduled ? (
                            <Chip
                              label={`Scheduled: ${new Date(counsellor.inductionScheduledDate!).toLocaleDateString()}`}
                              color="info"
                              size="small"
                            />
                          ) : (
                            <Chip label="Not Scheduled" color="default" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}


        {/* History Tab */}
        {activeTab === 2 && (
          <Box>
            {pastSessions.length === 0 ? (
              <Alert severity="info">No past induction sessions.</Alert>
            ) : (
              pastSessions.map((session) => (
                <Card key={session._id} sx={{ mb: 3, backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}>
                      {new Date(session.scheduledDate).toLocaleDateString('en-GB', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })} at {new Date(session.scheduledDate).toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
                      Attendees ({session.attendees.filter(a => a.attended).length}/{session.attendees.length} attended):
                    </Typography>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Name</TableCell>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Email</TableCell>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Status</TableCell>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Mark Attendance</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {session.attendees.map((attendee) => (
                            <TableRow key={attendee.counsellorId._id}>
                              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                {attendee.counsellorId.firstName} {attendee.counsellorId.lastName}
                              </TableCell>
                              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                {attendee.counsellorId.email}
                              </TableCell>
                              <TableCell>
                                {attendee.attended ? (
                                  <Chip
                                    label={`Attended ${new Date(attendee.attendedAt!).toLocaleDateString()}`}
                                    color="success"
                                    size="small"
                                    icon={<CheckCircleIcon />}
                                  />
                                ) : (
                                  <Chip label="Did Not Attend" color="error" size="small" icon={<CancelIcon />} />
                                )}
                              </TableCell>
                              <TableCell>
                                <Checkbox
                                  checked={attendee.attended}
                                  onChange={(e) => handleMarkAttended(session._id, attendee.counsellorId._id, e.target.checked)}
                                  icon={<CancelIcon />}
                                  checkedIcon={<CheckCircleIcon />}
                                  sx={{
                                    color: 'rgba(239, 83, 80, 0.9)',
                                    '&.Mui-checked': {
                                      color: 'rgba(76, 175, 80, 0.9)'
                                    }
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        )}

        {/* Create Session Dialog */}
        <Dialog 
          open={createSessionDialogOpen} 
          onClose={() => setCreateSessionDialogOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(30, 41, 59, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
            }
          }}
        >
          <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            Create Induction Session
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
                Induction Date & Time
              </Typography>
              <div className="relative">
                <DatePicker
                  selected={scheduledDate}
                  onChange={(date: Date | null) => setScheduledDate(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={30}
                  dateFormat="dd/MM/yyyy HH:mm"
                  placeholderText="Select date and time"
                  className="theme-input w-full p-3 pr-10 border rounded-lg"
                  popperClassName="datepicker-popper"
                  popperPlacement="bottom-start"
                  popperModifiers={[
                    {
                      name: 'preventOverflow',
                      options: {
                        rootBoundary: 'viewport',
                        tether: false,
                        altAxis: true,
                      },
                    },
                  ] as any}
                  minDate={new Date()}
                  minTime={
                    scheduledDate && 
                    scheduledDate.toDateString() === new Date().toDateString()
                      ? new Date()
                      : new Date(0, 0, 0, 0, 0)
                  }
                  maxTime={new Date(0, 0, 0, 23, 45)}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
                <CalendarToday 
                  onClick={() => {
                    const input = document.querySelector('.theme-input[placeholder="Select date and time"]') as HTMLInputElement;
                    if (input) input.focus();
                  }}
                  sx={{ 
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '20px',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    '&:hover': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    }
                  }} 
                />
              </div>

              <TextField
                label="Notes (Optional)"
                multiline
                rows={3}
                fullWidth
                margin="normal"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '&.Mui-focused': {
                      borderColor: 'rgba(100, 181, 246, 0.5)'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />

              <Alert 
                severity="info" 
                sx={{ 
                  mt: 2,
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  border: '1px solid rgba(33, 150, 243, 0.3)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  '& .MuiAlert-icon': {
                    color: 'rgba(100, 181, 246, 0.9)'
                  }
                }}
              >
                A Google Meet will be created. You can add counsellors to the session after creating it.
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 2 }}>
            <Button 
              onClick={() => setCreateSessionDialogOpen(false)} 
              disabled={loading}
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateSession} 
              variant="contained" 
              disabled={loading}
              sx={{
                backgroundColor: 'rgba(33, 150, 243, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(33, 150, 243, 1)'
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Session'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Counsellors Dialog */}
        <Dialog 
          open={addCounsellorsDialogOpen} 
          onClose={() => setAddCounsellorsDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(30, 41, 59, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
            }
          }}
        >
          <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            Add Counsellors to Induction
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
                Select counsellors to add to this induction session. Invitation emails will be sent automatically.
              </Typography>

              {selectedSession && (
                <>
                  {eligibleCounsellors.filter(c => !selectedSession.attendees.some(a => a.counsellorId._id === c._id)).length === 0 ? (
                    <Alert severity="info">All eligible counsellors are already in this session.</Alert>
                  ) : (
                    <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Table>
                        <TableHead sx={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
                          <TableRow>
                            <TableCell padding="checkbox" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                              <Checkbox
                                checked={selectedCounsellors.length > 0 && selectedCounsellors.length === eligibleCounsellors.filter(c => !selectedSession.attendees.some(a => a.counsellorId._id === c._id)).length}
                                indeterminate={selectedCounsellors.length > 0 && selectedCounsellors.length < eligibleCounsellors.filter(c => !selectedSession.attendees.some(a => a.counsellorId._id === c._id)).length}
                                onChange={handleSelectAll}
                              />
                            </TableCell>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Name</TableCell>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Email</TableCell>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Onboarding Completed</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {eligibleCounsellors
                            .filter(c => !selectedSession.attendees.some(a => a.counsellorId._id === c._id))
                            .map((counsellor) => (
                              <TableRow key={counsellor._id} hover>
                                <TableCell padding="checkbox" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                  <Checkbox
                                    checked={selectedCounsellors.includes(counsellor._id)}
                                    onChange={() => handleSelectCounsellor(counsellor._id)}
                                  />
                                </TableCell>
                                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                  {counsellor.firstName} {counsellor.lastName}
                                </TableCell>
                                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                  {counsellor.email}
                                </TableCell>
                                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                  {new Date(counsellor.onboardingCompletedAt).toLocaleDateString()}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 2 }}>
            <Button 
              onClick={() => setAddCounsellorsDialogOpen(false)} 
              disabled={loading}
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddCounsellors} 
              variant="contained" 
              disabled={loading || selectedCounsellors.length === 0}
              sx={{
                backgroundColor: 'rgba(33, 150, 243, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(33, 150, 243, 1)'
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : `Add ${selectedCounsellors.length} Counsellor(s)`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reschedule Dialog */}
        <Dialog 
          open={rescheduleDialogOpen} 
          onClose={() => setRescheduleDialogOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(30, 41, 59, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
            }
          }}
        >
          <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            Reschedule Induction
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
                New Date & Time
              </Typography>
              <div className="relative">
                <DatePicker
                  selected={scheduledDate}
                  onChange={(date: Date | null) => setScheduledDate(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={30}
                  dateFormat="dd/MM/yyyy HH:mm"
                  placeholderText="Select date and time"
                  className="theme-input w-full p-3 pr-10 border rounded-lg"
                  popperClassName="datepicker-popper"
                  popperPlacement="bottom-start"
                  popperModifiers={[
                    {
                      name: 'preventOverflow',
                      options: {
                        rootBoundary: 'viewport',
                        tether: false,
                        altAxis: true,
                      },
                    },
                  ] as any}
                  minDate={new Date()}
                  minTime={
                    scheduledDate && 
                    scheduledDate.toDateString() === new Date().toDateString()
                      ? new Date()
                      : new Date(0, 0, 0, 0, 0)
                  }
                  maxTime={new Date(0, 0, 0, 23, 45)}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
                <CalendarToday 
                  onClick={() => {
                    const inputs = document.querySelectorAll('.theme-input[placeholder="Select date and time"]');
                    const input = inputs[inputs.length - 1] as HTMLInputElement;
                    if (input) input.focus();
                  }}
                  sx={{ 
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '20px',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    '&:hover': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    }
                  }} 
                />
              </div>

              <Alert 
                severity="warning" 
                sx={{ 
                  mt: 2,
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  border: '1px solid rgba(255, 152, 0, 0.3)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  '& .MuiAlert-icon': {
                    color: 'rgba(255, 167, 38, 0.9)'
                  }
                }}
              >
                A new Google Meet will be created and rescheduling emails will be sent to all attendees.
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 2 }}>
            <Button 
              onClick={() => setRescheduleDialogOpen(false)} 
              disabled={loading}
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRescheduleInduction} 
              variant="contained" 
              disabled={loading}
              sx={{
                backgroundColor: 'rgba(33, 150, 243, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(33, 150, 243, 1)'
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Reschedule'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default InductionManagement;
